import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Encabezado.css";
import "../../styles/motion.css";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../ui/LanguageSelector";

interface SubService {
  label: string;
  to: string;
}

interface MenuItem {
  label: string;
  to: string;
  submenu?: SubService[];
  isTrigger?: boolean;
}

export default function Encabezado() {
  const [showMenu, setShowMenu] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const subServicios: SubService[] = [
    { label: t("nav.servicios_list.desarrollo"), to: "/desarrollo" },
    { label: t("nav.servicios_list.migracion"), to: "/migracion" },
    { label: t("nav.servicios_list.digitalizacion"), to: "/digitalizacion" },
    { label: t("nav.servicios_list.data"), to: "/data" },
    { label: t("nav.servicios_list.incident"), to: "/incident" },
    { label: t("nav.servicios_list.consultoria"), to: "/consultoria" },
    { label: t("nav.servicios_list.ia"), to: "/ia" },
    { label: t("nav.servicios_list.staff"), to: "/staffAugmentation" },
  ];

  const serviciosTriggerRef = useRef<HTMLAnchorElement>(null);
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const menuItems: MenuItem[] = [
    { label: t("nav.inicio"), to: "/" },
    { label: t("nav.nosotros"), to: "/empresa" },
    {
      label: t("nav.servicios"),
      to: "/servicios",
      submenu: subServicios,
      isTrigger: true,
    },
    { label: t("nav.postulate"), to: "/postulate" },
    { label: t("nav.contacto"), to: "/contacto" },
  ];

  useEffect(() => {
    const handleResize = () => {
      const isSmall = window.innerWidth <= 1024;
      setIsMobile(isSmall);
      if (!isSmall) setShowMenu(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setShowMenu(false);
    setOpenSubmenu(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const menuNav = target.closest("nav");
      const hamburger = target.closest(".hamburger-icon");
      const submenuContainer = target.closest(".menu-item-with-submenu");

      if (!menuNav && !hamburger) {
        setShowMenu(false);
      }

      if (!submenuContainer && !isMobile) {
        setOpenSubmenu(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMobile]);

  const toggleSubmenu = (label: string) => {
    if (isMobile) setOpenSubmenu(openSubmenu === label ? null : label);
  };

  const handleBlurContainer = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (!isMobile) setOpenSubmenu(null);
    }
  };

  const handleSubmenuKeyDown = (e: React.KeyboardEvent) => {
    if (isMobile) return;
    const key = e.key;
    const activeElement = document.activeElement as HTMLElement;
    const submenuLinks = Array.from(
      e.currentTarget.querySelectorAll<HTMLAnchorElement>("a.submenu-link"),
    );
    const currentIndex = submenuLinks.indexOf(
      activeElement as HTMLAnchorElement,
    );

    if (key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % submenuLinks.length;
      submenuLinks[nextIndex].focus();
    } else if (key === "ArrowUp") {
      e.preventDefault();
      const prevIndex =
        (currentIndex - 1 + submenuLinks.length) % submenuLinks.length;
      submenuLinks[prevIndex].focus();
    } else if (key === "Escape") {
      e.preventDefault();
      setOpenSubmenu(null);
      if (serviciosTriggerRef.current) serviciosTriggerRef.current.focus();
    }
  };

  const handleTriggerKeyDown = (
    e: React.KeyboardEvent,
    hasSubmenu: boolean,
    label: string,
  ) => {
    if (!hasSubmenu || isMobile) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpenSubmenu(label);
      setTimeout(() => {
        const firstLink = document.querySelector<HTMLAnchorElement>(
          "#submenu-servicios a.submenu-link",
        );
        if (firstLink) firstLink.focus();
      }, 50);
    }
  };

  const closeMenuOnMouseLeave = () => {
    if (isMobile) return;
    submenuTimeoutRef.current = setTimeout(() => {
      setOpenSubmenu(null);
    }, 500);
  };

  const openMenuOnMouseEnter = (item: MenuItem) => {
    if (!isMobile) {
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
      setOpenSubmenu(item.label);
    }
  };

  return (
    <header
      className={`header beam--large${isScrolled ? " header-scrolled" : ""}`}
    >
      <div className="header-content">
        <Link to="/" aria-label="Ir al inicio">
          <img
            src="/logo20_2.jpg"
            alt="Logo Consultoría Global"
            className="header-logo motion-hover-scale"
          />
        </Link>

        <div className="menu-container">
          {isMobile && (
            <button
              className={`hamburger-icon${showMenu ? " open" : ""}`}
              aria-label={showMenu ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={showMenu}
              onClick={() => setShowMenu((v) => !v)}
            >
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </button>
          )}

          <nav
            className={`menu-items${showMenu ? " show motion-fade-in" : ""}`}
            aria-label="Menú principal"
          >
            {menuItems.map((item) =>
              item.submenu ? (
                <div
                  key={item.label}
                  className={`menu-item-with-submenu ${openSubmenu === item.label ? "submenu-open" : ""}`}
                  onMouseEnter={() => openMenuOnMouseEnter(item)}
                  onMouseLeave={() => closeMenuOnMouseLeave()}
                  onFocus={() => !isMobile && setOpenSubmenu(item.label)}
                  onBlur={handleBlurContainer}
                  onKeyDown={handleSubmenuKeyDown}
                >
                  <Link
                    to={item.to}
                    className="menu-link motion-hover-scale"
                    aria-haspopup="true"
                    aria-expanded={openSubmenu === item.label}
                    aria-controls="submenu-servicios"
                    ref={item.isTrigger ? serviciosTriggerRef : null}
                    onKeyDown={(e) => handleTriggerKeyDown(e, true, item.label)}
                    onClick={(e) => {
                      if (isMobile) {
                        e.preventDefault();
                        toggleSubmenu(item.label);
                      } else {
                        setShowMenu(false);
                      }
                    }}
                  >
                    {item.label}
                  </Link>

                  <div
                    id="submenu-servicios"
                    className="submenu motion-fade-in-up"
                    role="menu"
                  >
                    {item.submenu.map((sub) => (
                      <Link
                        key={sub.label}
                        to={sub.to}
                        className="submenu-link"
                        role="menuitem"
                        tabIndex={openSubmenu === item.label ? 0 : -1}
                        onClick={() => {
                          setShowMenu(false);
                          setOpenSubmenu(null);
                        }}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className="menu-link motion-hover-scale"
                  onClick={() => setShowMenu(false)}
                >
                  {item.label}
                </Link>
              ),
            )}
            <LanguageSelector />
          </nav>
        </div>
      </div>
    </header>
  );
}
